import {
  useInfiniteQuery,
  type InfiniteData,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query'
import {type AppBskyFeedDefs} from '@atproto/api'

import {TapApiClient, type TapPost} from '#/lib/api/tap'
import {useAgent, useSession} from '#/state/session'
import {STALE} from '.'

const tapAllPostsQueryKeyRoot = 'tap-all-posts'
// Version added to clear stale cache after API format change
const tapAllPostsQueryKey = (did: string) => [tapAllPostsQueryKeyRoot, 'v2', did]

interface TapAllPostsResponse {
  posts: TapPost[]
  total: number
  limit: number
  offset: number
}

export function useTapAllPostsQuery(opts?: {enabled?: boolean}) {
  const {currentAccount} = useSession()
  const agent = useAgent()
  const did = currentAccount?.did ?? 'guest'

  console.log('[DEBUG tap-all-posts] Query setup:', {
    hasDid: !!did,
    did: did === 'guest' ? 'guest' : did.slice(0, 20) + '...',
    queryKey: [tapAllPostsQueryKeyRoot, did],
  })

  return useInfiniteQuery<
    TapAllPostsResponse,
    Error,
    InfiniteData<TapAllPostsResponse>,
    QueryKey,
    number
  >({
    enabled: opts?.enabled !== false,
    queryKey: tapAllPostsQueryKey(did),
    queryFn: async ({pageParam}) => {
      console.log('[tap-all-posts] Query function called:', {
        pageParam,
        hasAgent: !!agent,
      })

      const client = new TapApiClient()

      console.log('[tap-all-posts] Fetching from global videos endpoint (public):', {
        pageParam,
      })

      // OPTIMIZED: Single request to global videos endpoint
      // Posts are already video-only and globally sorted by backend
      // Note: This is a public endpoint, no auth token needed
      let response
      try {
        response = await client.getVideoFeed({
          limit: 50,
          offset: pageParam,
          // Don't pass accessToken - this is a public endpoint
        })
      } catch (error) {
        console.error('[tap-all-posts] Failed to fetch videos:', {
          error: error instanceof Error ? error.message : String(error),
        })
        throw error
      }

      console.log('[tap-all-posts] Got response from global endpoint:', {
        postsCount: response.posts?.length || 0,
        total: response.total,
      })

      // Posts are already video-only and globally sorted by backend
      // TAP backend provides playlist URLs but NOT complete author profiles or engagement metrics
      let posts = response.posts

      console.log('[tap-all-posts] Video embed analysis:', {
        totalPosts: posts.length,
        postsWithVideoEmbed: posts.filter(p => p.embed?.$type === 'app.bsky.embed.video#view').length,
        postsWithPlaylist: posts.filter(p => p.embed?.playlist).length,
      })

      // PROGRESSIVE LOADING: Fetch author profiles + engagement metrics from Bluesky
      // Strategy: Fetch first 25 posts immediately, then remaining 25 in background
      if (posts.length > 0 && agent) {
        try {
          const postUris = posts.map(post => post.uri)

          console.log('[tap-all-posts] Fetching complete author profiles and engagement metrics from Bluesky:', {
            totalPosts: postUris.length,
            strategy: 'First 25 immediate, remaining in background',
          })

          // Split into two batches for progressive loading
          const firstBatch = postUris.slice(0, 25)
          const secondBatch = postUris.slice(25, 50)

          // STEP 1: Fetch first 25 posts immediately
          console.log('[tap-all-posts] Fetching first batch (25 posts)...')
          const firstBatchResult = await agent.getPosts({uris: firstBatch})
          const firstBatchPosts = firstBatchResult.data.posts

          // Create map for first batch
          const blueskyPostMap = new Map(
            firstBatchPosts.map(p => [p.uri, p]),
          )

          console.log('[tap-all-posts] First batch fetched:', {
            requested: firstBatch.length,
            received: firstBatchPosts.length,
          })

          // STEP 2: Fetch second batch in background (don't await)
          if (secondBatch.length > 0) {
            console.log('[tap-all-posts] Starting background fetch for remaining posts...')
            agent.getPosts({uris: secondBatch}).then(result => {
              console.log('[tap-all-posts] Background batch fetched:', {
                requested: secondBatch.length,
                received: result.data.posts.length,
              })
              // Add to map (will be used if query refetches)
              result.data.posts.forEach(p => blueskyPostMap.set(p.uri, p))
            }).catch(error => {
              console.error('[tap-all-posts] Background fetch failed:', error)
            })
          }

          // Merge Bluesky data (author profiles + engagement metrics) with TAP data (playlist URLs + tags)
          for (let i = 0; i < posts.length; i++) {
            const tapPost = posts[i]
            const blueskyPost = blueskyPostMap.get(tapPost.uri)

            if (blueskyPost) {
              // Merge: Keep TAP's video embed with playlist URL, add Bluesky's author + engagement
              posts[i] = {
                ...tapPost, // Keep TAP data as base (includes playlist URLs from backend)
                author: blueskyPost.author, // Complete author profile with avatar
                viewer: blueskyPost.viewer, // Update viewer state
                likeCount: blueskyPost.likeCount, // Engagement metrics
                repostCount: blueskyPost.repostCount,
                replyCount: blueskyPost.replyCount,
                quoteCount: blueskyPost.quoteCount,
              }
            }
          }

          console.log('[tap-all-posts] Updated posts with Bluesky author + engagement data:', {
            enriched: posts.filter(p => p.author?.avatar).length,
            total: posts.length,
          })
        } catch (error) {
          console.error('[tap-all-posts] Failed to fetch Bluesky data:', error)
          // Continue with TAP data - videos will still play with placeholder avatars
        }
      } else if (!agent) {
        console.log('[tap-all-posts] No agent available - displaying with TAP data only (no author avatars or engagement metrics)')
      }

      // Note: Posts are already sorted by backend (indexed_at DESC)
      // No need to sort again on the client side

      return {
        posts: posts,
        total: response.total,
        limit: response.limit,
        offset: pageParam,
      }
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const currentOffset = allPages.length * 50
      return currentOffset < lastPage.total ? currentOffset : undefined
    },
    staleTime: STALE.SECONDS.THIRTY,
  })
}

export function* findAllPostsInQueryData(
  queryClient: QueryClient,
  uri: string,
): Generator<AppBskyFeedDefs.PostView, void> {
  const queryDatas = queryClient.getQueriesData<
    InfiniteData<TapAllPostsResponse>
  >({
    queryKey: [tapAllPostsQueryKeyRoot],
  })
  for (const [_queryKey, queryData] of queryDatas) {
    if (!queryData?.pages) {
      continue
    }
    for (const page of queryData.pages) {
      for (const post of page.posts) {
        if (post.uri === uri) {
          yield post
        }
      }
    }
  }
}
